import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  X, Users, LayoutGrid, List, Plus, Search, Phone, Mail, MessageSquare, Trash2,
  FileText, Wrench, ListChecks, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { CUSTOMER_STATUSES } from "@/lib/config";
import { Card, Badge, Btn, EmptyState, inputCls, labelCls, AInput, ASelect, useSave } from "../AdminUI";

const VIEW_KEY = "vpw_crm_view";

const stageColor = (idx, total) => {
  if (idx >= total - 2) return idx === total - 1 ? "red" : "green";
  if (idx <= 1) return "blue";
  return "amber";
};

function CustomerDetail({ id, stages, canDelete, onClose, onChange }) {
  const [c, setC] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [note, setNote] = useState("");
  const [saving, run] = useSave();
  const load = () => api.get(`/admin/customers/${id}`).then((r) => setC(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => { api.get(`/admin/invoices?customer_id=${id}`).then((r) => setInvoices(r.data)).catch(() => setInvoices([])); }, [id]);
  if (!c) return null;

  const patch = (body, msg = "Updated") => run(async () => {
    await api.patch(`/admin/customers/${id}`, body); await load(); onChange?.();
  }, msg);
  const addNote = () => { if (!note.trim()) return; run(async () => {
    await api.post(`/admin/customers/${id}/notes`, { text: note }); setNote(""); await load();
  }, "Note added"); };
  const del = () => { if (!window.confirm("Permanently delete this customer? This cannot be undone.")) return;
    run(async () => { await api.delete(`/admin/customers/${id}`); onClose(); onChange?.(); }, "Customer deleted"); };

  const Field = ({ label, value }) => value ? <div><p className="text-[11px] uppercase text-gray-400 font-semibold">{label}</p><p className="text-sm text-[var(--navy)]">{value}</p></div> : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="customer-detail">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[var(--surface)] h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between z-10">
          <div><h2 className="font-head font-bold text-xl text-[var(--navy)]">{c.name || "Customer"}</h2>
            <p className="text-xs text-gray-400">{c.customer_id} · {c.status}</p></div>
          <button onClick={onClose} data-testid="customer-detail-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-sm bg-[var(--red)] text-white px-3 py-1.5 rounded-md"><Phone className="w-4 h-4" /> Call</a>}
            {c.phone && <a href={`sms:${c.phone}`} className="flex items-center gap-1.5 text-sm bg-[var(--action)] text-white px-3 py-1.5 rounded-md"><MessageSquare className="w-4 h-4" /> Text</a>}
            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-sm bg-[var(--navy)] text-white px-3 py-1.5 rounded-md"><Mail className="w-4 h-4" /> Email</a>}
          </div>

          <Card title="Pipeline">
            <div className="grid grid-cols-2 gap-3">
              <ASelect label="CRM Stage" value={c.crm_stage} options={stages} onChange={(e) => patch({ crm_stage: e.target.value }, "Stage updated")} data-testid="customer-stage-select" />
              <ASelect label="Status" value={c.status} options={CUSTOMER_STATUSES} onChange={(e) => patch({ status: e.target.value })} data-testid="customer-status-select" />
              <AInput label="Assigned To" defaultValue={c.assigned_to} onBlur={(e) => patch({ assigned_to: e.target.value })} />
              <AInput label="Follow-up Date" type="date" defaultValue={c.follow_up_date} onBlur={(e) => patch({ follow_up_date: e.target.value })} />
              <AInput label="Estimated Value ($)" type="number" defaultValue={c.estimated_value || ""} onBlur={(e) => patch({ estimated_value: parseFloat(e.target.value) || 0 })} />
              <AInput label="Lead Source" defaultValue={c.lead_source} onBlur={(e) => patch({ lead_source: e.target.value })} />
            </div>
          </Card>

          <Card title="Contact & Property">
            <div className="grid grid-cols-2 gap-3">
              <AInput label="First Name" defaultValue={c.first_name} onBlur={(e) => patch({ first_name: e.target.value })} />
              <AInput label="Last Name" defaultValue={c.last_name} onBlur={(e) => patch({ last_name: e.target.value })} />
              <AInput label="Phone" defaultValue={c.phone} onBlur={(e) => patch({ phone: e.target.value })} />
              <AInput label="Email" defaultValue={c.email} onBlur={(e) => patch({ email: e.target.value })} />
              <AInput label="Company" defaultValue={c.company_name} onBlur={(e) => patch({ company_name: e.target.value })} />
              <AInput label="Address" defaultValue={c.address} onBlur={(e) => patch({ address: e.target.value })} />
              <AInput label="City" defaultValue={c.city} onBlur={(e) => patch({ city: e.target.value })} />
              <AInput label="ZIP" defaultValue={c.zip} onBlur={(e) => patch({ zip: e.target.value })} />
            </div>
          </Card>

          {c.linked_lead_ids?.length > 0 && (
            <Card title="Linked Leads & Sources">
              <div className="flex flex-wrap gap-2">
                {c.linked_lead_ids.map((lid) => <Badge key={lid} color="blue">{lid}</Badge>)}
                {c.conversation_ids?.filter(Boolean).map((cv) => <Badge key={cv} color="navy">Ghost AI: {cv.slice(0, 8)}</Badge>)}
              </div>
            </Card>
          )}

          <Card title={<span className="flex items-center gap-2"><FileText className="w-4 h-4" /> Invoices ({invoices.length})</span>}>
            {invoices.length ? invoices.map((iv) => (
              <div key={iv.id} className="flex justify-between text-sm py-1.5 border-b border-gray-50 last:border-0" data-testid={`cust-invoice-${iv.invoice_number}`}>
                <span>{iv.invoice_number} · {iv.status}</span>
                <span className="text-gray-500">${iv.total?.toFixed(2)} · bal ${iv.balance_due?.toFixed(2)}</span>
              </div>
            )) : <p className="text-sm text-gray-400">No invoices.</p>}
          </Card>

          <Card title={<span className="flex items-center gap-2"><FileText className="w-4 h-4" /> Quotes ({c.quotes?.length || 0})</span>}>
            {c.quotes?.length ? c.quotes.map((q) => (
              <div key={q.id} className="flex justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                <span>{q.quote_id} · {q.status}</span><span className="font-semibold">${q.total?.toFixed(2)}</span>
              </div>
            )) : <p className="text-sm text-gray-400">No quotes.</p>}
          </Card>

          <Card title={<span className="flex items-center gap-2"><Wrench className="w-4 h-4" /> Jobs ({c.jobs?.length || 0})</span>}>
            {c.jobs?.length ? c.jobs.map((j) => (
              <div key={j.id} className="flex justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                <span>{j.job_id} · {j.status}</span><span className="text-gray-400">{j.scheduled_date || "Unscheduled"}</span>
              </div>
            )) : <p className="text-sm text-gray-400">No jobs.</p>}
          </Card>

          <Card title={<span className="flex items-center gap-2"><ListChecks className="w-4 h-4" /> Follow-Up Tasks ({c.tasks?.length || 0})</span>}>
            {c.tasks?.length ? c.tasks.map((t) => (
              <div key={t.id} className="flex justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                <span>{t.title}</span><Badge color={t.status === "Completed" ? "green" : t.status === "Overdue" ? "red" : "amber"}>{t.status}</Badge>
              </div>
            )) : <p className="text-sm text-gray-400">No tasks.</p>}
          </Card>

          <Card title="Notes">
            <div className="flex gap-2">
              <input className={inputCls} placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="customer-note-input" onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <Btn onClick={addNote} loading={saving} data-testid="customer-add-note">Add</Btn>
            </div>
            <div className="mt-3 space-y-2">
              {(c.notes || []).slice().reverse().map((n, i) => <div key={i} className="text-sm bg-gray-50 rounded-md p-2.5"><p>{n.text}</p><p className="text-[11px] text-gray-400">{n.by} · {new Date(n.at).toLocaleString()}</p></div>)}
            </div>
          </Card>

          <Card title={<span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Activity Timeline</span>}>
            <div className="space-y-2">
              {(c.activity || []).slice().reverse().map((a, i) => (
                <div key={i} className="flex gap-3 text-sm"><span className="text-gray-400 text-xs w-32 shrink-0">{new Date(a.at).toLocaleString()}</span><span>{a.text}</span></div>
              ))}
            </div>
          </Card>

          {canDelete && (
            <div className="pt-2">
              <Btn variant="danger" onClick={del} data-testid="customer-delete"><Trash2 className="w-4 h-4" /> Delete Customer</Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewCustomerModal({ onClose, onCreated }) {
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", company_name: "", address: "", city: "", zip: "" });
  const [saving, run] = useSave();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); run(async () => {
    const r = await api.post("/admin/customers", f); onCreated(r.data.id); onClose();
  }, "Customer created"); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="new-customer-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h2 className="font-head font-bold text-xl text-[var(--navy)] uppercase">New Customer</h2>
        <div className="grid grid-cols-2 gap-3">
          <AInput label="First Name" value={f.first_name} onChange={set("first_name")} required data-testid="cust-first" />
          <AInput label="Last Name" value={f.last_name} onChange={set("last_name")} data-testid="cust-last" />
          <AInput label="Phone" value={f.phone} onChange={set("phone")} data-testid="cust-phone" />
          <AInput label="Email" value={f.email} onChange={set("email")} data-testid="cust-email" />
          <AInput label="Company" value={f.company_name} onChange={set("company_name")} />
          <AInput label="Address" value={f.address} onChange={set("address")} />
          <AInput label="City" value={f.city} onChange={set("city")} />
          <AInput label="ZIP" value={f.zip} onChange={set("zip")} />
        </div>
        <div className="flex gap-2 justify-end">
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" loading={saving} data-testid="cust-save">Create</Btn>
        </div>
      </form>
    </div>
  );
}

export default function Customers({ canDelete }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [stages, setStages] = useState([]);
  // URL ?view=pipeline|customers takes precedence, else saved per-admin preference, else pipeline
  const urlView = searchParams.get("view");
  const initialView = urlView === "customers" ? "list" : urlView === "pipeline" ? "kanban"
    : (localStorage.getItem(VIEW_KEY) || "kanban");
  const [view, setView] = useState(initialView);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = () => api.get(`/admin/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setCustomers(r.data)).catch(() => {});
  useEffect(() => { api.get("/admin/crm/stages").then((r) => setStages(r.data.stages)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  useEffect(() => {
    // keep internal view in sync when URL changes (e.g. redirect from old bookmarks)
    const uv = searchParams.get("view");
    if (uv === "customers" && view !== "list") setView("list");
    if (uv === "pipeline" && view !== "kanban") setView("kanban");
  }, [searchParams]); // eslint-disable-line

  const setViewPref = (v) => {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
    setSearchParams({ view: v === "list" ? "customers" : "pipeline" }, { replace: true });
  };

  const moveStage = async (cid, stage) => {
    setCustomers((cs) => cs.map((c) => c.id === cid ? { ...c, crm_stage: stage } : c));
    try { await api.patch(`/admin/customers/${cid}`, { crm_stage: stage }); toast.success(`Moved to ${stage}`); }
    catch (e) { toast.error("Move failed — check permissions"); load(); }
  };

  const filtered = useMemo(() => stageFilter ? customers.filter((c) => c.crm_stage === stageFilter) : customers, [customers, stageFilter]);

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkStage = async (stage) => {
    await Promise.all(selected.map((id) => api.patch(`/admin/customers/${id}`, { crm_stage: stage })));
    toast.success(`${selected.length} moved to ${stage}`); setSelected([]); load();
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} customers permanently?`)) return;
    await Promise.all(selected.map((id) => api.delete(`/admin/customers/${id}`)));
    toast.success("Deleted"); setSelected([]); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">CRM</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-gray-200 overflow-hidden" role="tablist">
            <button onClick={() => setViewPref("kanban")} className={`px-4 py-2 flex items-center gap-1.5 text-sm font-semibold ${view === "kanban" ? "bg-[var(--navy)] text-white" : "bg-white text-gray-500"}`} data-testid="crm-tab-pipeline"><LayoutGrid className="w-4 h-4" /> Pipeline</button>
            <button onClick={() => setViewPref("list")} className={`px-4 py-2 flex items-center gap-1.5 text-sm font-semibold ${view === "list" ? "bg-[var(--navy)] text-white" : "bg-white text-gray-500"}`} data-testid="crm-tab-customers"><List className="w-4 h-4" /> Customer List</button>
          </div>
          <Btn onClick={() => setShowNew(true)} data-testid="new-customer-btn"><Plus className="w-4 h-4" /> New</Btn>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input className={`${inputCls} pl-9`} placeholder="Search name, email, phone, address, ZIP…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search" />
        </div>
        <select className={`${inputCls} w-auto`} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="customer-stage-filter">
          <option value="">All stages</option>{stages.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-[var(--navy)] text-white rounded-lg px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm font-semibold">{selected.length} selected</span>
          <select className="h-8 rounded px-2 text-sm text-[var(--navy)]" onChange={(e) => e.target.value && bulkStage(e.target.value)} defaultValue="" data-testid="bulk-stage">
            <option value="" disabled>Move to stage…</option>{stages.map((s) => <option key={s}>{s}</option>)}
          </select>
          {canDelete && <button onClick={bulkDelete} className="text-sm text-red-200 hover:text-white flex items-center gap-1" data-testid="bulk-delete"><Trash2 className="w-4 h-4" /> Delete</button>}
          <button onClick={() => setSelected([])} className="text-sm text-white/60 ml-auto">Clear</button>
        </div>
      )}

      {customers.length === 0 ? (
        <Card><EmptyState icon={Users} title="No customers yet" hint="Convert a lead or create a customer to get started." /></Card>
      ) : view === "kanban" ? (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {stages.map((s, idx) => {
              const items = customers.filter((c) => c.crm_stage === s);
              return (
                <div key={s} className="w-64 shrink-0" data-testid={`kanban-col-${s.replace(/\s+/g, "-")}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragId) { moveStage(dragId, s); setDragId(null); } }}>
                  <div className="flex items-center justify-between px-2 py-1.5 mb-2">
                    <span className="text-xs font-bold uppercase text-[var(--navy)] truncate">{s}</span>
                    <Badge color={stageColor(idx, stages.length)}>{items.length}</Badge>
                  </div>
                  <div className="space-y-2 min-h-[60px]">
                    {items.map((c) => (
                      <div key={c.id} draggable onDragStart={() => setDragId(c.id)} onClick={() => setOpenId(c.id)}
                        className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-[var(--action)] active:cursor-grabbing" data-testid={`kanban-card-${c.customer_id}`}>
                        <p className="font-semibold text-sm text-[var(--navy)] truncate">{c.name || "—"}</p>
                        <p className="text-xs text-gray-400 truncate">{c.phone || c.email || c.customer_id}</p>
                        <div className="mt-2 sm:hidden">
                          <select className="w-full h-8 text-xs rounded border border-gray-200" value={c.crm_stage} onClick={(e) => e.stopPropagation()} onChange={(e) => moveStage(c.id, e.target.value)} data-testid={`mobile-stage-${c.customer_id}`}>
                            {stages.map((st) => <option key={st}>{st}</option>)}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto -m-1">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100">
                <th className="py-2 px-2 w-8"></th><th className="px-2">Customer</th><th className="px-2">Contact</th><th className="px-2">Stage</th><th className="px-2">Status</th><th className="px-2">Value</th>
              </tr></thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`customer-row-${c.customer_id}`}>
                    <td className="px-2"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSel(c.id)} data-testid={`select-${c.customer_id}`} /></td>
                    <td className="py-3 px-2 cursor-pointer" onClick={() => setOpenId(c.id)}><p className="font-semibold text-[var(--navy)]">{c.name || "—"}</p><p className="text-xs text-gray-400">{c.customer_id}</p></td>
                    <td className="px-2 text-gray-600 cursor-pointer" onClick={() => setOpenId(c.id)}>{c.phone || c.email || "—"}</td>
                    <td className="px-2"><Badge color="blue">{c.crm_stage}</Badge></td>
                    <td className="px-2 text-gray-600">{c.status}</td>
                    <td className="px-2 text-gray-600">{c.estimated_value ? `$${c.estimated_value}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {openId && <CustomerDetail id={openId} stages={stages} canDelete={canDelete} onClose={() => setOpenId(null)} onChange={load} />}
      {showNew && <NewCustomerModal onClose={() => setShowNew(false)} onCreated={(id) => { load(); setOpenId(id); }} />}
    </div>
  );
}
